import { getLoggerInstance } from '@idmc-ms-fabric/common';
import { ServiceRegistry } from '@infa-hawk/services';
import { IGetConnectionRequest, IGetConnectionResponse } from '@infa-hawk/services/src/services/fabric-be-service/models';
import { catalogSourceApi, catalogSourceExecutionApi, catalogSourceWithMultipartApi } from '@infa-service/cdgc-catalog';
import { isEmpty, snakeCase } from 'lodash-es';

import { SupportedArtifactType } from '../common-types';
import { CatalogSource, SupportedCatalogSourceType } from './catalog-source';
import { ICatalogSource, IReferenceResponse, MetadataFilterType } from './catalog-source-model';
import OracleCatalogSource from './oracle-catalog-source';
import WarehouseCatalogSource from './warehouse-catalog-source';

type SubCatalogSourceType = new (...args: ConstructorParameters<typeof CatalogSource>) => CatalogSource;

type TConnectionDetails = IGetConnectionResponse & { stagingConnectionId: string; stagingRuntimeEnv: string };

class CatalogSourceBuilder {
	private connections: Partial<Record<string, TConnectionDetails>> = {};
	private artifactTypeClassMap: Record<SupportedArtifactType, SubCatalogSourceType> = {
		'Oracle': OracleCatalogSource,
		'data-warehouse': WarehouseCatalogSource,
		'Warehouse': WarehouseCatalogSource,
	};

	private catalogSrcTypeClassMap: Record<SupportedCatalogSourceType, SubCatalogSourceType> = {
		'Oracle': OracleCatalogSource,
		'Fabric Data Warehouse': WarehouseCatalogSource,
	};

	private generateCSName(srcArtifactId: string, type: string) {
		type = snakeCase(type);
		return `${type}-${srcArtifactId}`;
	}

	private async fetchConnection(req: IGetConnectionRequest): Promise<TConnectionDetails> {
		const logger = getLoggerInstance({});
		const { fabricBeService, agentService } = ServiceRegistry.get();
		try {
			const resp = await fabricBeService.getConnection(req);
			logger.info('connection setup api');
			if (!resp.connectionId) {
				throw new Error('Fetching Connection details failed.');
			}
			const agentDetails = await agentService.getAgentDetails({ agentId: resp.runtimeEnvId });
			const { agentGroupId } = agentDetails;
			if (!agentGroupId) {
				throw new Error('Agent Group not found');
			}
			return { ...resp, stagingConnectionId: resp.connectionId, stagingRuntimeEnv: agentGroupId };
		} catch (err) {
			logger.error('Fetching connection details failed');
			throw new Error('Fetching connection details failed.');
		}
	}

	private async getConnection(req: Required<IGetConnectionRequest>) {
		const connectionId = req.connectionId as SupportedArtifactType;
		let connectionDetails = this.connections[connectionId];
		if (!connectionDetails) {
			connectionDetails = await this.fetchConnection(req);
			this.connections[connectionId] = connectionDetails;
		}
		return connectionDetails;
	}

	private async getCatalogSource(
		objectType: SupportedArtifactType,
		input: ICatalogSource | { objectId: string; workspaceId: string; workspaceName: string; accessToken: string },
	) {
		const Clz = this.artifactTypeClassMap[objectType];
		if ('objectId' in input) {
			const connectionDetails = await this.getConnection({
				accessToken: input.accessToken,
				workspaceId: input.workspaceId,
				workspaceName: input.workspaceName,
				connectionType: objectType === 'Warehouse' ? 'Warehouse' : 'Lakehouse',
				connectionId: input.objectId,
			});
			const csName = this.generateCSName(input.objectId, objectType);
			return new Clz({
				connectionDetails: {
					srcConnection: connectionDetails.connectionId,
					stagingConnection: connectionDetails.stagingConnectionId,
					runtimeEnv: connectionDetails.stagingRuntimeEnv,
				},
				csName,
			});
		} else {
			return this.getCatalogSourceByInput(input as ICatalogSource);
		}
	}

	public getCatalogSourceByInput(input: ICatalogSource) {
		const Clz = this.catalogSrcTypeClassMap[input.type as SupportedCatalogSourceType];
		return new Clz(input);
	}

	async create(type: SupportedArtifactType, srcArtifactId: string, workspaceId: string, workspaceName: string, accessToken: string, orgId: string) {
		const csName = this.generateCSName(srcArtifactId, type);
		const logger = getLoggerInstance({});
		let new_source = true;
		let response: IReferenceResponse, payload: ICatalogSource;
		try {
			/**
			 * * check if a data source already exists for selected warehouse/lakehouse
			 */
			response = await this.getCatalogSourceByName(csName);
			const cs = await this.getCatalogSource(type, response);
			payload = cs.getCreatePayload();
			new_source = false;
		} catch (error) {
			logger.error('Fetch CS failed with', error);
			const cs = await this.getCatalogSource(type, { objectId: srcArtifactId, workspaceId, workspaceName, accessToken });

			payload = cs.getCreatePayload();
			try {
				response = await catalogSourceWithMultipartApi.createDataSourceMultipart({
					dataSource: payload,
				});
			} catch (error) {
				logger.error('data source creation failed');
				throw error;
			}
		}
		return {
			payload,
			response,
			new_source,
		};
	}
	async getCatalogSourceByName(datasourceName: string) {
		const { datasources } = await catalogSourceApi.getDataSources({ filter: [`name:EQ:${datasourceName}`] });
		if (datasources && !isEmpty(datasources)) {
			return datasources[0];
		} else {
			throw new Error('No cataloge source with given name');
		}
	}

	async delete(type: SupportedArtifactType, srcArtifactId: string) {
		const datasourceName = this.generateCSName(srcArtifactId, type);
		await catalogSourceApi.deleteDataSource({
			datasourceId: datasourceName,
		});
	}

	getCriteria(type: SupportedCatalogSourceType, filterValue: string) {
		if (type === SupportedCatalogSourceType.Oracle) {
			return filterValue;
		}
		return filterValue;
	}

	async rerun(name: string, filterValue: string, filterType: MetadataFilterType) {
		const csResponse = await this.getCatalogSourceByName(name);
		const cs = await this.getCatalogSourceByInput(csResponse);
		const criteria = this.getCriteria(cs.getType(), filterValue);
		const execPayload = cs.getSyncProfilingPayload(criteria, filterType);
		const execResp = await catalogSourceExecutionApi.syncDataSource({
			datasourceId: csResponse.id as string,
			dataSourceConfigObject: {
				...execPayload,
				CMSWorkFlowName: 'ccgf-catalog-source-sync-workflow',
			},
		});
		return { repsonse: execResp, criteria };
	}
}

const catalogSourceBuilder = new CatalogSourceBuilder();
export default catalogSourceBuilder;
